import { useEffect, useMemo, useState } from "react";
import {
  getConcurrentPlayDetectedProblem,
  resolveConcurrentPlay,
  type ConcurrentPlayDetectedProblem,
  type ConcurrentPlayResolution,
} from "../../api/concurrent-play";
import { useCreatePlayline } from "../../api/hooks/useCreatePlayline";
import { useScenario } from "../../api/hooks/useScenario";
import { ApiError } from "../../api/client";
import { loadClientEnv } from "../../config/env";
import { mapProblemDetail } from "../../errors/mapProblemDetail";
import { playlineStorage } from "../../playline/storage";
import { ConcurrentPlayDialog } from "../concurrent-play/ConcurrentPlayDialog";
import { useModelSelector } from "../debug/useModelSelector";
import { ErrorBanner } from "../player/ErrorBanner";
import {
  FixedContentUnavailable,
  ReaderGenerationMode,
} from "../player/ReaderPlaybackStatus";
import { ScenarioIntroOverlay } from "../player/ScenarioIntroOverlay";

export type ScenarioStartProps = {
  /** 作成成功時に playlineId を親へ通知する。状態マシンを Playing に遷移させる想定。 */
  onStarted?: (playlineId: string) => void;
  /** 開始するシナリオ ID。省略時は env.demoScenarioId を使用。 */
  scenarioId?: string;
};

/**
 * デモシナリオ開始導線（Req 1.1, 1.2, 1.3）。
 *
 * - 状態マシンのエントリポイントであり、親が mutation を所有する理由が無いため
 *   `useCreatePlayline()` をこのコンポーネント内で直接呼び出す。
 * - `VITE_DEMO_SCENARIO_ID` は `loadClientEnv()` 経由で取得し、欠落時は
 *   初期化段階で throw させる（env.ts の契約）。
 * - エラーは `mapProblemDetail` で UIError に正規化して `ErrorBanner` に委譲し、
 *   表示・再試行の分岐を ErrorBanner の retryable/requiresRestart ロジックに一本化する。
 */
export function ScenarioStart({ onStarted, scenarioId: scenarioIdProp }: ScenarioStartProps): JSX.Element {
  // ビルド時に固定された環境変数のため、render ごとに再評価しないようメモ化する。
  const env = useMemo(() => loadClientEnv(), []);
  const scenarioId = scenarioIdProp ?? env.demoScenarioId;
  const mutation = useCreatePlayline();
  const { selectedModel } = useModelSelector();
  const [concurrentProblem, setConcurrentProblem] =
    useState<ConcurrentPlayDetectedProblem | null>(null);

  // scenario-start-ux (Phase 4): scenario を prefetch し、overlay に渡すため
  // ScenarioStart 自身でも useScenario を呼ぶ。staleTime Infinity なので PlayerScreen 側
  // で再 fetch されずキャッシュ共有される。
  const scenarioQuery = useScenario(scenarioId);

  // 成功通知は副作用として実行する。render 中に親コールバックを呼ぶと
  // React の警告（setState in render）を踏む可能性があるため。
  useEffect(() => {
    if (mutation.isSuccess && mutation.data) {
      onStarted?.(mutation.data.id);
    }
  }, [mutation.isSuccess, mutation.data, onStarted]);

  useEffect(() => {
    if (!mutation.isError) return;
    const problem = getConcurrentPlayDetectedProblem(mutation.error);
    if (problem === null) return;

    setConcurrentProblem(problem);
    mutation.reset();
  }, [mutation]);

  const handleStart = (): void => {
    setConcurrentProblem(null);
    mutation.mutate({ scenarioId });
  };

  const handleResolveConcurrentPlay = async (
    resolution: ConcurrentPlayResolution,
  ): Promise<void> => {
    if (concurrentProblem === null) return;

    const playline = await resolveConcurrentPlay(env.apiBaseUrl, {
      resolution,
      existingPlaylineId: concurrentProblem.existingPlaylineId,
      scenarioId,
      model: selectedModel,
    });
    playlineStorage.set(playline.id, playline.scenarioId);
    setConcurrentProblem(null);
    onStarted?.(playline.id);
  };

  const uiError = mutation.isError
    ? mapProblemDetail(mutation.error)
    : undefined;
  const isFixedContentUnavailableProblem =
    mutation.error instanceof ApiError &&
    mutation.error.problem.type === "/errors/fixed-content-unavailable";
  const isResolvingFixedContentUnavailable =
    isFixedContentUnavailableProblem &&
    scenarioQuery.data === undefined &&
    scenarioQuery.isFetching;
  const isFixedContentUnavailable =
    isFixedContentUnavailableProblem &&
    scenarioQuery.data?.generationMode === "pre-baked";

  // scenario-start-ux: mutation.isPending かつ error なしの間は overlay を表示する。
  // flag off 時は ScenarioIntroOverlay 内で LoadingOverlay に delegate される。
  const showIntro = mutation.isPending && !mutation.isError;

  return (
    <section
      aria-label="シナリオ開始"
      className="flex flex-col items-center gap-4 p-6"
    >
      <h1 className="text-xl font-semibold">新しい物語を始める</h1>
      <p className="text-sm text-reader-secondary">
        準備ができたら下のボタンから物語を開始してください。
      </p>
      <ReaderGenerationMode generationMode={scenarioQuery.data?.generationMode} />
      <button
        type="button"
        onClick={handleStart}
        disabled={
          mutation.isPending ||
          isResolvingFixedContentUnavailable ||
          isFixedContentUnavailable
        }
        className="min-h-11 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded px-4 py-2 font-medium"
      >
        開始
      </button>
      <div className="flex gap-4">
        <a
          href="/search"
          className="text-sm text-reader-secondary hover:text-reader-primary underline underline-offset-2"
        >
          他のシナリオを探す
        </a>
        <a
          href="/library"
          className="text-sm text-reader-secondary hover:text-reader-primary underline underline-offset-2"
        >
          ライブラリ
        </a>
      </div>
      {isFixedContentUnavailable ? (
        <FixedContentUnavailable />
      ) : isResolvingFixedContentUnavailable ? null : uiError ? (
        // 再試行は「もう一度開始を押した」のと等価なため、同じ handler を渡す。
        <ErrorBanner error={uiError} onRetry={handleStart} />
      ) : null}
      {showIntro && (
        <ScenarioIntroOverlay
          isPending={true}
          phase="creating-playline"
          scenario={scenarioQuery.data}
          scenarioErrored={scenarioQuery.isError}
        />
      )}
      {concurrentProblem !== null ? (
        <ConcurrentPlayDialog
          existingPlaylineId={concurrentProblem.existingPlaylineId}
          existingLastPlayedAt={concurrentProblem.existingLastPlayedAt}
          scenarioTitle={scenarioQuery.data?.title ?? null}
          onResolve={handleResolveConcurrentPlay}
          onCancel={() => setConcurrentProblem(null)}
        />
      ) : null}
    </section>
  );
}
